/**
 * Shim de compatibilidade react-router-dom v5 -> v7 para class components.
 *
 * Em v5 a API era baseada em HOC (`withRouter`). Em v6/v7 o HOC sumiu.
 * Mantemos o shim porque a codebase tem ~118 usos de `withRouter` e ~92
 * acessos a `props.match.params` em class components, que nao podem
 * consumir hooks diretamente.
 *
 * Function components devem usar os hooks nativos `useNavigate` /
 * `useLocation` / `useParams` direto de `react-router-dom`. O hook
 * `useHistory` foi removido deste shim em 2026-05-09 (Fase 2.1).
 *
 * Uso:
 *   import { withRouter } from '<path>/utils/withRouterCompat';
 *
 * `withRouter(Component)` injeta { history, location, match } como em v5.
 *
 * APIs avancadas de v5 (history.listen / history.block / history.createHref)
 * NAO sao emuladas — busca pre-migracao confirmou zero uso na codebase.
 */
import React from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'

function buildHistory(navigate, location) {
  return {
    push: (to, state) => {
      if (typeof to === 'string') return navigate(to, { state })
      return navigate(to.pathname ?? to, { state: to.state ?? state })
    },
    replace: (to, state) => {
      if (typeof to === 'string') return navigate(to, { replace: true, state })
      return navigate(to.pathname ?? to, {
        replace: true,
        state: to.state ?? state,
      })
    },
    goBack: () => navigate(-1),
    go: (delta) => navigate(delta),
    location,
  }
}

export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const navigate = useNavigate()
    const params = useParams()
    const location = useLocation()

    // Memoiza history e match para preservar a estabilidade de referência
    // entre renders. Sem isso, cada render do componente envolto em withRouter
    // recebia history/match novos, invalidando React.memo / useCallback que
    // dependem dessas props — causava re-render em cascata na árvore inteira
    // (visível especialmente no DashboardGeral, que tem accordions aninhados).
    const history = React.useMemo(
      () => buildHistory(navigate, location),
      [navigate, location],
    )
    const match = React.useMemo(
      () => ({
        params,
        path: location.pathname,
        url: location.pathname,
        isExact: true,
      }),
      [params, location.pathname],
    )

    return (
      <Component
        {...props}
        history={history}
        location={location}
        match={match}
      />
    )
  }
  ComponentWithRouterProp.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`
  return ComponentWithRouterProp
}
